import type { Metric } from "web-vitals";
import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";

// Google Core Web Vitals thresholds
// https://web.dev/vitals/
const thresholds = {
  LCP: { good: 2500, needsImprovement: 4000 },
  INP: { good: 200, needsImprovement: 500 },
  CLS: { good: 0.1, needsImprovement: 0.25 },
  FCP: { good: 1800, needsImprovement: 3000 },
  TTFB: { good: 800, needsImprovement: 1800 },
};

type MetricName = keyof typeof thresholds;

export const getRating = (
  name: MetricName,
  value: number
): "good" | "needs-improvement" | "poor" => {
  const threshold = thresholds[name];
  if (!threshold) {
    return "good";
  }
  if (value <= threshold.good) {
    return "good";
  }
  if (value <= threshold.needsImprovement) {
    return "needs-improvement";
  }
  return "poor";
};

export const ratingColors = {
  good: "#0CCE6B",
  "needs-improvement": "#FFA400",
  poor: "#FF4E42",
};

// Reporter type
type VitalsReporter = (metric: Metric) => void;

// Current reporter (can be set later)
let currentReporter: VitalsReporter | null = null;

/**
 * Console reporter - logs metrics with color-coded rating
 */
export const consoleReporter: VitalsReporter = (metric) => {
  const rating = getRating(metric.name as MetricName, metric.value);
  const color = ratingColors[rating];

  const valueFormatted =
    metric.name === "CLS"
      ? metric.value.toFixed(3)
      : `${Math.round(metric.value)}ms`;

  console.log(
    `%c[Web Vitals] ${metric.name}: ${valueFormatted} (${rating})`,
    `color: ${color}; font-weight: bold;`
  );
};

/**
 * Batched reporter - collects metrics and sends in batches
 */
export const createBatchedReporter = (
  sendBatch: (metrics: Metric[]) => void,
  batchSize = 5
): VitalsReporter => {
  const batch: Metric[] = [];

  return (metric) => {
    batch.push(metric);
    if (batch.length >= batchSize) {
      sendBatch([...batch]);
      batch.length = 0;
    }
  };
};

/**
 * PerfMetricsMonitor reporter — feeds the live debug panel store
 */
export const createPerfMonitorReporter = (
  reportVital: (metric: Metric) => void
): VitalsReporter => {
  return (metric) => {
    reportVital(metric);
  };
};

/**
 * Composite reporter - sends to multiple destinations
 */
export const createCompositeReporter = (
  ...reporters: VitalsReporter[]
): VitalsReporter => {
  return (metric) => {
    reporters.forEach((reporter) => reporter(metric));
  };
};

/**
 * Set the reporter to use
 */
export const setReporter = (reporter: VitalsReporter) => {
  currentReporter = reporter;
};

/**
 * Internal handler that forwards to the current reporter
 */
const handleMetric = (metric: Metric) => {
  if (currentReporter) {
    currentReporter(metric);
  }
};

/**
 * Initialize web vitals collection
 * Call this once on app startup
 */
export const initWebVitals = () => {
  // Default to console in dev; prod vitals reach Sentry via browserTracingIntegration
  if (!currentReporter) {
    currentReporter = import.meta.env.DEV ? consoleReporter : null;
  }

  // Collect all Core Web Vitals
  onLCP(handleMetric);
  onINP(handleMetric);
  onCLS(handleMetric);
  onFCP(handleMetric);
  onTTFB(handleMetric);

  console.log("📊 Web Vitals monitoring initialized");
};

/**
 * Get metric summary for debugging
 */
export const getVitalsThresholds = () => thresholds;
